import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { TextField } from 'formik-mui';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { useAdminUpdatePasswordMutation } from '../services/users.endpoints';
import { NewPasswordSchema } from '../schemas/users.schemas';

interface AdminEditPasswordProps {
  userId: string;
}

const AdminEditPassword: React.FC<AdminEditPasswordProps> = ({ userId }) => {
  const [adminUpdatePassword, { error, isSuccess, isLoading }] =
    useAdminUpdatePasswordMutation();
  return (
    <Formik
      initialValues={{
        newPassword: '',
        confirmPassword: '',
        showPassword: false,
      }}
      validate={toValidator(NewPasswordSchema)}
      onSubmit={(values) => {
        adminUpdatePassword({ userId, newPassword: values.newPassword });
      }}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <Grid container spacing={3}>
            <Grid item>
              <Typography variant="h5">Change Password</Typography>
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{formatQueryError(error)}</Alert>
              </Grid>
            )}
            {isSuccess && (
              <Grid item xs={12}>
                <Alert severity="success">
                  Successfully edited password! User can now log in with this
                  password.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Field
                component={TextField}
                name="newPassword"
                type={values.showPassword ? undefined : 'password'}
                label="New Password"
                disabled={isLoading}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          values.showPassword
                            ? 'Hide password'
                            : 'Show password'
                        }
                      >
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setFieldValue('showPassword', !values.showPassword)
                          }
                        >
                          {values.showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                component={TextField}
                name="confirmPassword"
                type={values.showPassword ? undefined : 'password'}
                label="Confirm Password"
                disabled={isLoading}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Edit Password'}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default AdminEditPassword;
